/* eslint-disable no-console */
import { authenticationConfig } from "app-config";
import type User from "models/User";
import graphService from "services/graphService";
import type { RedirectRequest, SilentRequest } from "@azure/msal-browser";
import { msalInstance } from "providers/Authentication/SecuAuthentificationProvider";
import storageService from "./storageService";
import { authConfig } from "@sdeapps/react-core";

/**
 * Redirige l'utilisateur vers la page de login Microsoft.
 */
async function login(): Promise<void> {
  if (msalInstance == null) {
    throw new Error("msalInstance is null !");
  }

  try {
    await msalInstance.loginRedirect({
      ...authenticationConfig.msal.auth,
      account: msalInstance.getActiveAccount() ?? undefined,
      scopes: authConfig.graph.scopes,
    });
  } catch (error) {
    console.error(error);
  }
}

/**
 * Redirige l'utilisateur vers la page de logout Microsoft
 */
async function logout(): Promise<void> {
  if (msalInstance == null) {
    throw new Error("msalInstance is null !");
  }

  try {
    await msalInstance.logoutRedirect({
      ...authenticationConfig.msal.auth,
      account: msalInstance.getActiveAccount() ?? undefined,
    });
  } catch (error) {
    console.error(error);
  }
}

/**
 * Récupère les données sur le compte actuellement authentifié, et logge
 * l'utilisateur si aucun compte n'est actuellement authentifié.
 */
async function getAccessToken(scopes: Array<string>): Promise<string> {
  if (msalInstance == null) {
    throw new Error("msalInstance is null !");
  }

  const activeAccount = msalInstance.getActiveAccount();
  const accounts = msalInstance.getAllAccounts();

  if (activeAccount == null && accounts.length === 0) {
    console.info("%cNo active account! Initiating login.", "color: cornflowerblue;");
    await login();
  }

  const request: SilentRequest | RedirectRequest = {
    ...authenticationConfig.msal.auth,
    scopes,
    account: activeAccount ?? accounts[0],
  };

  try {
    const authResult = await msalInstance?.acquireTokenSilent({
      ...request,
    });
    if (authResult == null) {
      throw new Error("No auth token found");
    }
    return authResult.accessToken;
  } catch (error) {
    const e1 = error as Error;
    console.log("%cFail acquireTokenSilent initial", "color: cornflowerblue;", e1.name, e1.message);
    console.log(e1);
    try {
      console.info("%cTrying acquireTokenRedirect", "color: cornflowerblue;");
      await msalInstance?.acquireTokenRedirect(request);
    } catch (error2) {
      const e2 = error2 as Error;
      console.info("%cFail acquireTokenRedirect : ", "color: cornflowerblue;", e2.name, e2.message);
      // au fail du acquireTokenRedirect, il y a un problème d'authentification.
      // On va clear le local et session storage pour effacer toute trace
      // d'authentification incomplète, obsolète ou erronée, et recommencer.

      console.info("%cCLEANING LOCAL AND SESSION STORAGE", "color: cornflowerblue;");
      storageService.clearLocalAndSessionStorage();

      try {
        console.info("%cRetrying acquireTokenRedirect again", "color: cornflowerblue;");
        await msalInstance?.acquireTokenRedirect(request);
      } catch (error3) {
        // Si ça pèche toujours, on ne peut plus faire grand-chose d'autre que tenter
        // de hard reloger l'utilisateur.
        console.info("%cFail acquireTokenRedirect again : ", "color: cornflowerblue;", error3);
        console.info("%cRetrying login", "color: cornflowerblue;");
        await login();
        const e3 = error3 as Error;
        throw new Error(e3.name + e3.message, { cause: error3 });
      }
    }
  }

  const error = Error("ACQUIRE TOKEN ATTEMPTS ALL FAILED MISERABLY");
  console.error(error);
  throw error;
}

/**
 * Cherche les données dans l'AD et l'avatar de l'utilisateur actuellement authentifié.
 */
async function getUserInfo(): Promise<User> {
  const userInfo: User = await graphService.getUserInfo();
  try {
    userInfo.avatar = await graphService.getUserPhoto();
  } catch (error) {
    console.error("error getting user photo : ", error);
  }
  return userInfo;
}

const authService = {
  login,
  logout,
  getAccessToken,
  getUserInfo,
};
export default authService;
